import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "@heroui/react";
import { BlacklistAPI } from "@repo/api/blacklist";
import type { BlacklistItem } from "@repo/api/blacklist.type";
import { blacklistKeys, useRefetchOnToken } from "@repo/query";

type ErrorLike = { msg?: string } | null | undefined;

/** 我订阅的黑名单集合。订阅页与可信查询面板共用，同时挂载只请求一次。 */
export function useSubscribedBlacklists(enabled = true) {
  return useQuery({
    queryKey: blacklistKeys.subscriptions(),
    queryFn: () => BlacklistAPI.getSubscribedBlacklists(),
    enabled,
  });
}

/** 公共黑名单集合（分页），翻页保留上一页数据。`is_subscribed` 由接口内部结合订阅列表算出。 */
export function usePublicBlacklists(page: number, pageSize: number, enabled = true) {
  return useQuery({
    queryKey: blacklistKeys.shared({ page, pageSize }),
    queryFn: () => BlacklistAPI.getPublicBlacklists(page, pageSize),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/** 公共黑名单集合（无限滚动，H5 用）。到最后一页即停止。 */
export function usePublicBlacklistsInfinite(pageSize: number, enabled = true) {
  return useInfiniteQuery({
    queryKey: blacklistKeys.shared({ pageSize }),
    queryFn: ({ pageParam }) => BlacklistAPI.getPublicBlacklists(pageParam, pageSize),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      pages.length < lastPage.totalPages ? pages.length + 1 : undefined,
    enabled,
  });
}

/**
 * 单个公共黑名单集合的元信息。
 * 预览页在接口失败时仍要能渲染标题，所以这里把失败兜底成只有 id / name 的占位对象。
 */
export function useBlacklistSet(id: string | undefined) {
  return useQuery({
    queryKey: blacklistKeys.sharedSet(Number(id)),
    queryFn: async (): Promise<BlacklistItem> => {
      try {
        return await BlacklistAPI.getBlacklistItem(id!);
      } catch {
        return { id: id!, name: "黑名单预览" } as BlacklistItem;
      }
    },
    enabled: Boolean(id),
  });
}

export type BlacklistUserFilters = { uid?: string; reason?: string; onlyWithEvidence?: boolean };

/**
 * 公共黑名单集合内的用户（无限滚动，H5 用）。筛选条件进 key，变化即从第一页重拉。
 * 请求失败交给调用方处理（H5 预览页失败要退回列表页）。
 */
export function useBlacklistSetUsersInfinite(
  id: string | undefined,
  pageSize: number,
  filters: BlacklistUserFilters,
) {
  return useInfiniteQuery({
    queryKey: blacklistKeys.sharedSetUsers(Number(id), { pageSize, ...filters }),
    queryFn: ({ pageParam }) =>
      BlacklistAPI.getBlacklistUsers(id!, pageParam, pageSize, {
        uid: filters.uid || undefined,
        reason: filters.reason || undefined,
        onlyWithEvidence: filters.onlyWithEvidence,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      pages.length < lastPage.totalPages ? pages.length + 1 : undefined,
    enabled: Boolean(id),
  });
}

/** 我的黑名单（无限滚动，H5 用） */
export function useMyBlacklistInfinite(pageSize: number, enabled = true) {
  return useInfiniteQuery({
    queryKey: blacklistKeys.mine({ pageSize }),
    queryFn: ({ pageParam }) => BlacklistAPI.getMyBlacklist(pageParam, pageSize),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      pages.length < lastPage.totalPages ? pages.length + 1 : undefined,
    enabled,
  });
}

/** 我的黑名单公开设置（标题 / 是否公开） */
export function useMyBlacklistSettings(enabled = true) {
  return useQuery({
    queryKey: blacklistKeys.mySettings(),
    queryFn: () => BlacklistAPI.getBlacklistPublicSettings(),
    enabled,
  });
}

/** 订阅 / 取消订阅。成功后整个黑名单域失效：订阅列表、公共列表的 is_subscribed、命中检查都会刷新。 */
export function useSubscribeBlacklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (setId: string) => BlacklistAPI.subscribeBlacklist(setId),
    onSuccess: () => {
      toast.success("订阅成功");
      void queryClient.invalidateQueries({ queryKey: blacklistKeys.all });
    },
    onError: (error: ErrorLike) => toast.danger(error?.msg || "订阅失败"),
  });
}

export function useUnsubscribeBlacklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (setId: string) => BlacklistAPI.unsubscribeBlacklist(setId),
    onSuccess: () => {
      toast.success("已取消订阅");
      void queryClient.invalidateQueries({ queryKey: blacklistKeys.all });
    },
    onError: (error: ErrorLike) => toast.danger(error?.msg || "取消失败"),
  });
}

/** 把用户加入我的黑名单（公共黑名单预览页「一键拉黑」/ 可信查询 / 添加表单共用） */
export function useAddToBlacklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { uid: string; reason?: string; images?: string[] }) =>
      BlacklistAPI.addToBlacklist(params.uid, { reason: params.reason, images: params.images }),
    onSuccess: () => {
      toast.success("已添加至黑名单");
      void queryClient.invalidateQueries({ queryKey: blacklistKeys.all });
    },
    onError: (error: ErrorLike) => toast.danger(error?.msg || "添加失败"),
  });
}

export function useRemoveFromBlacklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uid: string) => BlacklistAPI.removeFromBlacklist(uid),
    onSuccess: () => {
      toast.success("已移出黑名单");
      void queryClient.invalidateQueries({ queryKey: blacklistKeys.all });
    },
    onError: (error: ErrorLike) => toast.danger(error?.msg || "移除失败"),
  });
}

export function useUpdateBlacklistEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { uid: string; reason?: string; images: string[] }) =>
      BlacklistAPI.updateBlacklistEntry(params.uid, {
        reason: params.reason,
        images: params.images,
      }),
    onSuccess: () => {
      toast.success("已保存");
      void queryClient.invalidateQueries({ queryKey: blacklistKeys.all });
    },
    onError: (error: ErrorLike) => toast.danger(error?.msg || "保存失败"),
  });
}

export function useUpdateBlacklistSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { title: string; is_public: boolean }) =>
      BlacklistAPI.updateBlacklistSettings(params),
    onSuccess: () => {
      toast.success("设置已更新");
      void queryClient.invalidateQueries({ queryKey: blacklistKeys.mySettings() });
    },
    onError: (error: ErrorLike) => toast.danger(error?.msg || "更新失败"),
  });
}

/** 某 UID 的黑名单命中检查（可信查询）；`queryToken` 变化时强制重查一次 */
export function useBlacklistCheck(uid: string | undefined, queryToken?: number) {
  const query = useQuery({
    queryKey: blacklistKeys.check(uid ?? ""),
    queryFn: () => BlacklistAPI.checkUid(uid!),
    enabled: Boolean(uid),
  });
  useRefetchOnToken(query.refetch, queryToken);
  return query;
}
